import { useEffect, useState } from 'react';
import { useImagePicker } from '@/features/common/components/ImagePicker/useImagePicker';
import { ExperienceImageTypes } from '@/types/gql.generated';
import { useToast } from '@/ui';
import { useEffectiveExperience } from '../contexts/PageExperienceContext';
import { useRemoveImageForExperience } from './useRemoveImageForExperience';
import { useUploadImageForExperience } from './useUploadImageForExperience';

/**
 * The author's logo, on the experience that owns it — the planner rather than
 * the view being edited, so every view draws the same mark and uploading from
 * any of them targets the parent.
 *
 * `preview` is what to render: the picked file while the upload is in flight, so
 * a slot fills in immediately rather than after the round trip, rolled back to
 * the saved image if the write fails. Both actions cross-disable while either is
 * pending — they write the same field, so whichever request lands last would
 * otherwise win over whatever the author clicked last.
 *
 * Calls its mutation hooks unconditionally, so mount this behind an author check
 * only: a read-only render such as the printable/PDF iframe has its own React
 * root with no QueryClientProvider and would throw (WEB-CLIENT-2AC).
 */
export const useExperienceLogo = () => {
  const toast = useToast();
  const { open: openImagePicker } = useImagePicker();
  const experience = useEffectiveExperience();
  const logoImageUrl = experience.logoImageUrl ?? null;

  const [preview, setPreview] = useState(logoImageUrl);

  const { uploadAsset, isPending: isUploading } = useUploadImageForExperience(
    experience.id
  );
  const { removeAsset, isPending: isRemoving } =
    useRemoveImageForExperience(experience);

  useEffect(() => {
    setPreview(logoImageUrl);
  }, [logoImageUrl]);

  const pickLogo = () =>
    openImagePicker({
      onSelect: (file) => {
        const previousPreview = preview;
        const fileReader = new FileReader();
        fileReader.onloadend = () => setPreview(fileReader.result as string);
        fileReader.readAsDataURL(file);

        uploadAsset(ExperienceImageTypes.Logo, file, {
          onError: () => {
            toast.error('Error saving logo');
            setPreview(previousPreview);
          },
        });
      },
    });

  const removeLogo = () => {
    const previousPreview = preview;
    setPreview(null);

    removeAsset(ExperienceImageTypes.Logo, {
      onError: () => {
        toast.error('Error removing logo');
        setPreview(previousPreview);
      },
    });
  };

  return { preview, isUploading, isRemoving, pickLogo, removeLogo };
};
